import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, AtSign, Shield, Lock, CheckCircle2, AlertTriangle, Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * AccountSettingsPage — account-level identity settings for any logged-in
 * user. Today it hosts the self-service account email change. Password
 * reset still flows through the dedicated /forgot-password route.
 *
 * NOTE on the two-email concept:
 *   • Account/Login Email → stored on the user record. Authenticates logins
 *     and receives transactional messages. This page edits that one.
 *   • Public Contact Email → stored on the company profile (/portal/profile).
 *     Optional, shown on the public business directory. Separate on purpose.
 */
const AccountSettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center text-slate-500">
        Loading your account…
      </div>
    );
  }

  const normalized = newEmail.trim().toLowerCase();
  const validShape = EMAIL_RE.test(normalized);
  const sameAsCurrent = normalized === (user.email || '').toLowerCase();
  const canSubmit = validShape && !sameAsCurrent && currentPassword.length > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/portal/me/email`,
        { new_email: normalized, current_password: currentPassword },
        { withCredentials: true },
      );
      toast.success(`Your account email is now ${data.email}`);
      setNewEmail('');
      setCurrentPassword('');
      await refreshUser?.();
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 401) {
        toast.error(detail || 'Current password is incorrect.');
      } else if (code === 409) {
        toast.error(detail || 'That email already belongs to another account.');
      } else if (code === 400) {
        toast.error(detail || 'Please provide a valid email address.');
      } else {
        toast.error('Could not update your email — please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8" data-testid="account-settings-page">
      <Link
        to="/portal/profile"
        className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
        data-testid="back-to-profile"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to profile
      </Link>

      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Account Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage the identity your Chamber account uses to sign in.
          </p>
        </div>
      </div>

      {/* ─── Account email card ─────────────────────────────────────────── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <AtSign className="w-4 h-4 text-[#002249]" />
          <h2 className="font-bold text-[#1a1a1a]">Account email</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          This is the email you use to sign in and where your transactional
          messages are delivered. It's separate from the public contact email
          shown on your business directory listing.
        </p>

        <div className="mb-5">
          <Label className="text-xs text-slate-500 uppercase tracking-wide">Current email</Label>
          <div
            className="mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono text-slate-700 break-all"
            data-testid="current-account-email"
          >
            {user.email}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-email-form">
          <div>
            <Label htmlFor="new-email" className="text-xs text-slate-500 uppercase tracking-wide">
              New email
            </Label>
            <div className="mt-1 relative">
              <AtSign className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-9"
                autoComplete="email"
                data-testid="self-new-email"
              />
            </div>
            {newEmail && !validShape && (
              <p className="text-xs text-rose-600 mt-1">Please enter a valid email address.</p>
            )}
            {sameAsCurrent && (
              <p className="text-xs text-amber-700 mt-1">That's the email you already use.</p>
            )}
          </div>

          <div>
            <Label htmlFor="current-password" className="text-xs text-slate-500 uppercase tracking-wide">
              Confirm with your current password
            </Label>
            <div className="mt-1 relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Your current password"
                className="pl-9"
                autoComplete="current-password"
                data-testid="self-current-password"
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              We never show or store this field anywhere — it's only used to
              verify it's you.
            </p>
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-[#002249] hover:bg-[#0a3570]"
            data-testid="self-change-email-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update account email'}
          </Button>
        </form>
      </section>

      {/* ─── Context cards ─────────────────────────────────────────────── */}
      <section className="mt-6 grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="font-semibold text-sm text-[#1a1a1a]">What stays the same</span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Your password, membership status, posts, comments, committees,
            email preferences and history all stay connected to your account.
            Only the login email changes.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="font-semibold text-sm text-[#1a1a1a]">Right after you save</span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Your old email can no longer sign in to this account. Make sure
            you have access to the new inbox before saving.
          </p>
        </div>
      </section>

      {/* ─── Deep-link to Email Preferences ────────────────────────────── */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm text-[#1a1a1a]">Email preferences</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Choose which Chamber emails you want to receive.
          </div>
        </div>
        <Link
          to="/portal/email-preferences"
          className="text-sm font-semibold text-[#002249] hover:underline"
          data-testid="go-to-email-prefs"
        >
          Manage →
        </Link>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
